/* eslint-disable react-refresh/only-export-components */
import React, { Suspense } from 'react';
import { createBrowserRouter, Navigate, useNavigate } from 'react-router';
import * as Sentry from '@sentry/react';
import { BASE as FARM_WORKSPACE_BASE, LEGACY_HUB_PATHS } from './components/FarmWorkspace/workspaceRoutes';
import { AppLayout } from './components/Layout/AppLayout';
import { RouteSuspenseFallback } from './components/Layout/RouteSuspenseFallback';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { BillingIaRedirect, BillingIaSwitch, SmartSamplingExternalRoute } from './components/Portal/PortalRoutes';
// Tiny and already a static dependency of PortalRoutes — lazy-loading it would be an ineffective split.
import { PortalPlaceholderPage } from './components/Portal/PortalPlaceholderPage';

// Eager-loaded page components (critical path)
import { WorkflowDashboard } from './components/Workflow';

// Role-adaptive analytics board — the post-login landing. Lazy so chart.js lands
// in its own chunk rather than the entry bundle.
const AnalyticsDashboard = lazyWithRetry(() => import('./components/Analytics/AnalyticsDashboard'), 'AnalyticsDashboard');

// Lazy-loaded page components (auto-retry on stale chunk errors)
const TabbedImportPage = lazyWithRetry(() => import('./components/Import/TabbedImportPage'), 'TabbedImportPage');
const AgentsList = lazyWithRetry(() => import('./components/LookupTables/Agents/AgentsList'), 'AgentsList');
const LabsList = lazyWithRetry(() => import('./components/LookupTables/Labs/LabsList'), 'LabsList');
const CropTypesList = lazyWithRetry(() => import('./components/LookupTables/CropTypes/CropTypesList'), 'CropTypesList');
const CropSubTypesList = lazyWithRetry(() => import('./components/LookupTables/CropSubTypes/CropSubTypesList'), 'CropSubTypesList');
const SampleTypesList = lazyWithRetry(() => import('./components/LookupTables/SampleTypes/SampleTypesList'), 'SampleTypesList');
const RatioConfigurationsList = lazyWithRetry(() => import('./components/LookupTables/RatioConfigurations/RatioConfigurationsList'), 'RatioConfigurationsList');
const SageSettingsPage = lazyWithRetry(() => import('./components/Sage/SageSettingsPage'), 'SageSettingsPage');
const ClaudeSettingsPage = lazyWithRetry(() => import('./components/Claude/ClaudeSettingsPage'), 'ClaudeSettingsPage');
const CalendarSettingsPage = lazyWithRetry(() => import('./components/Settings/CalendarSettingsPage'), 'CalendarSettingsPage');
const UserManagementPage = lazyWithRetry(() =>
  import('./components/UserManagement/UserManagementPage').then(mod => ({ default: mod.UserManagementPage })),
  'UserManagementPage',
);
const ProfilePage = lazyWithRetry(() =>
  import('./components/Profile/ProfilePage').then(mod => ({ default: mod.ProfilePage })),
  'ProfilePage',
);
const NormsPage = lazyWithRetry(() => import('./pages/NormsPage'), 'NormsPage');
const BatchesPage = lazyWithRetry(() => import('./components/Batches/BatchesPage'), 'BatchesPage');
const WorkspacePage = lazyWithRetry(() => import('./components/Batches/WorkspacePage'), 'WorkspacePage');
const FarmersList = lazyWithRetry(() => import('./components/Farmers/FarmersList'), 'FarmersList');
const FarmsList = lazyWithRetry(() => import('./components/Farms/FarmsList'), 'FarmsList');
const FarmPlotsList = lazyWithRetry(() => import('./components/FarmPlots/FarmPlotsList'), 'FarmPlotsList');
const FarmWorkspacePage = lazyWithRetry(() => import('./components/FarmWorkspace/FarmWorkspacePage'), 'FarmWorkspacePage');
const HybridNormsCalculationPage = lazyWithRetry(() => import('./components/HybridNorms/HybridNormsCalculationPage'), 'HybridNormsCalculationPage');
const HybridNormsViewPage = lazyWithRetry(() => import('./components/HybridNorms/HybridNormsViewPage'), 'HybridNormsViewPage');
const SettingsPage = lazyWithRetry(() => import('./components/Settings/SettingsPage'), 'SettingsPage');
const MessageTemplatesPage = lazyWithRetry(() => import('./components/Settings/MessageTemplatesPage'), 'MessageTemplatesPage');
const DrisCalculationSettingsPage = lazyWithRetry(() => import('./components/Settings/DrisCalculationSettingsPage'), 'DrisCalculationSettingsPage');
const HybridElementBreakpointsTab = lazyWithRetry(() => import('./components/Settings/HybridElementBreakpointsTab'), 'HybridElementBreakpointsTab');
const HybridProductFactorsTab = lazyWithRetry(() => import('./components/Settings/HybridProductFactorsTab'), 'HybridProductFactorsTab');
const RecalculateSamplesPage = lazyWithRetry(() => import('./components/Settings/RecalculateSamplesPage'), 'RecalculateSamplesPage');
const ElementRankingsPage = lazyWithRetry(() => import('./components/Settings/ElementRankingsPage'), 'ElementRankingsPage');
const CompressionTrainingPage = lazyWithRetry(() => import('./components/Settings/CompressionTrainingPage'), 'CompressionTrainingPage');
const LegacyCalculatorPage = lazyWithRetry(() => import('./components/LegacyCalculator/LegacyCalculatorPage'), 'LegacyCalculatorPage');
const WorkflowDetailPage = lazyWithRetry(() => import('./components/Workflow/WorkflowDetailPage/WorkflowDetailPage'), 'WorkflowDetailPage');

const RoleManagement = lazyWithRetry(() =>
  import('./components/Admin/RoleManagement').then(mod => ({ default: mod.RoleManagement })),
  'RoleManagement',
);
const WorkflowPermissions = lazyWithRetry(() =>
  import('./components/Admin/WorkflowPermissions').then(mod => ({ default: mod.WorkflowPermissions })),
  'WorkflowPermissions',
);
const MenuPermissions = lazyWithRetry(() =>
  import('./components/Admin/MenuPermissions').then(mod => ({ default: mod.MenuPermissions })),
  'MenuPermissions',
);
const AccessGrants = lazyWithRetry(() =>
  import('./components/Admin/AccessGrants').then(mod => ({ default: mod.AccessGrants })),
  'AccessGrants',
);
const ReportAccessAudit = lazyWithRetry(() =>
  import('./components/Admin/ReportAccessAudit').then(mod => ({ default: mod.ReportAccessAudit })),
  'ReportAccessAudit',
);
const NotificationsPage = lazyWithRetry(() => import('./components/Notifications/NotificationsPage'), 'NotificationsPage');
const StagingReviewPage = lazyWithRetry(() => import('./components/Staging/StagingReviewPage'), 'StagingReviewPage');
const ConsolidatedDashboard = lazyWithRetry(() => import('./components/Finance/ConsolidatedDashboard'), 'ConsolidatedDashboard');
const ConsolidatedClientDetailPage = lazyWithRetry(() => import('./components/Finance/ConsolidatedClientDetailPage'), 'ConsolidatedClientDetailPage');
const ConsolidatedPeriodDetailPage = lazyWithRetry(() => import('./components/Finance/ConsolidatedPeriodDetailPage'), 'ConsolidatedPeriodDetailPage');
const PeriodRedirect = lazyWithRetry(() => import('./components/Finance/components/PeriodRedirect'), 'PeriodRedirect');

// Agent Portal (external users) — index-free read surface
const AgentPortalDashboard = lazyWithRetry(() => import('./components/AgentPortal/AgentPortalDashboard'), 'AgentPortalDashboard');
const CustomerHome = lazyWithRetry(() => import('./components/AgentPortal/CustomerHome'), 'CustomerHome');
const AgentPortalWorkspace = lazyWithRetry(() => import('./components/AgentPortal/AgentPortalWorkspace'), 'AgentPortalWorkspace');
const MyFarmersPage = lazyWithRetry(() => import('./components/Portal/MyFarmersPage'), 'MyFarmersPage');
const PortalProfilePage = lazyWithRetry(() => import('./components/Portal/Profile/PortalProfilePage'), 'PortalProfilePage');
const FarmsPage = lazyWithRetry(() => import('./components/Portal/FarmsPage'), 'FarmsPage');
const OnboardingPage = lazyWithRetry(() => import('./components/Portal/OnboardingPage'), 'OnboardingPage');
const AnalyseExistingResultsPage = lazyWithRetry(() => import('./components/Portal/AnalyseExistingResultsPage'), 'AnalyseExistingResultsPage');
// New External IA (portal monetisation, plan §4): A1 welcome + placeholders later phases replace.
const WelcomePage = lazyWithRetry(() => import('./components/Portal/WelcomePage'), 'WelcomePage');
const BuyAnalysesPage = lazyWithRetry(() => import('./components/Portal/Buy/BuyAnalysesPage'), 'BuyAnalysesPage');
const BuyReturnPage = lazyWithRetry(() => import('./components/Portal/Buy/BuyReturnPage'), 'BuyReturnPage');

// Partner Portal (partner-lab users) — submit + read own/granted results via /api/partner/v1
const PartnerPortalDashboard = lazyWithRetry(() => import('./components/PartnerPortal/PartnerPortalDashboard'), 'PartnerPortalDashboard');
const PartnerResultsList = lazyWithRetry(() => import('./components/PartnerPortal/PartnerResultsList'), 'PartnerResultsList');
const PartnerResultsWorkspace = lazyWithRetry(() => import('./components/PartnerPortal/PartnerResultsWorkspace'), 'PartnerResultsWorkspace');
const PartnerSubmitPage = lazyWithRetry(() => import('./components/PartnerPortal/PartnerSubmitPage'), 'PartnerSubmitPage');
const PartnerEntitiesPage = lazyWithRetry(() => import('./components/PartnerPortal/PartnerEntitiesPage'), 'PartnerEntitiesPage');
const PartnerApiKeysPage = lazyWithRetry(() => import('./components/PartnerPortal/PartnerApiKeysPage'), 'PartnerApiKeysPage');
const PartnerWorkflowCreatePage = lazyWithRetry(() => import('./components/PartnerPortal/PartnerWorkflowCreatePage'), 'PartnerWorkflowCreatePage');
const PartnerWorkflowsPage = lazyWithRetry(() => import('./components/PartnerPortal/PartnerWorkflowsPage'), 'PartnerWorkflowsPage');
const PartnerProductsPage = lazyWithRetry(() => import('./components/PartnerPortal/PartnerProductsPage'), 'PartnerProductsPage');
const PartnerTeamPage = lazyWithRetry(() => import('./components/PartnerPortal/PartnerTeamPage'), 'PartnerTeamPage');
const PartnerStagingReviewPage = lazyWithRetry(() => import('./components/PartnerPortal/PartnerStagingReviewPage'), 'PartnerStagingReviewPage');
const CommunicationPage = lazyWithRetry(() => import('./components/Communication/CommunicationPage'), 'CommunicationPage');
const IntakeReviewPage = lazyWithRetry(() => import('./components/Communication/IntakeReview/IntakeReviewPage'), 'IntakeReviewPage');
const SmartSamplingListPage = lazyWithRetry(() => import('./components/SmartSampling/SmartSamplingListPage'), 'SmartSamplingListPage');
const SmartSamplingWorkspace = lazyWithRetry(() => import('./components/SmartSampling/SmartSamplingWorkspace'), 'SmartSamplingWorkspace');
// SS External self-service (Phase 4a) — the /portal/smart-sampling surface (list + landing, new run,
// per-run workspace). Gated dark behind `smartSamplingExternalEnabled` (SmartSamplingExternalRoute).
const ExternalSmartSamplingListPage = lazyWithRetry(() => import('./components/SmartSampling/external/ExternalSmartSamplingListPage'), 'ExternalSmartSamplingListPage');
const ExternalSmartSamplingWorkspace = lazyWithRetry(() => import('./components/SmartSampling/external/ExternalSmartSamplingWorkspace'), 'ExternalSmartSamplingWorkspace');
const ExternalNewRunPage = lazyWithRetry(() => import('./components/SmartSampling/external/ExternalNewRunPage'), 'ExternalNewRunPage');
const FertilizerProgramsPage = lazyWithRetry(() => import('./components/Admin/FertilizerProgramsPage'), 'FertilizerProgramsPage');
const DataExtractionPage = lazyWithRetry(() => import('./components/Admin/DataExtractionPage'), 'DataExtractionPage');
const DuplicateScanPage = lazyWithRetry(() => import('./components/Admin/DuplicateScanPage'), 'DuplicateScanPage');
const BillingProductsPage = lazyWithRetry(() => import('./components/Admin/BillingProductsPage'), 'BillingProductsPage');
const FertilizerProgramBuilderPage = lazyWithRetry(() => import('./components/Admin/FertilizerPrograms/FertilizerProgramBuilderPage'), 'FertilizerProgramBuilderPage');

// Sample Intake (authed portal, free-tier) — submit + history/reprint
// Pathway ① is now the profile-driven redesign (PortalSampleIntakePage); the legacy nested free-text
// form is kept reachable at /portal/intake-legacy for rollback until the redesign is live-validated.
const PortalSampleIntakePage = lazyWithRetry(() => import('./components/Portal/PortalSampleIntakePage'), 'PortalSampleIntakePage');
const PortalIntakePage = lazyWithRetry(() => import('./components/Intake/PortalIntakePage'), 'PortalIntakePage');
const MySubmissionsPage = lazyWithRetry(() => import('./components/Intake/MySubmissionsPage'), 'MySubmissionsPage');
const SamplesPage = lazyWithRetry(() => import('./components/Portal/SamplesPage'), 'SamplesPage');
const ReportsPage = lazyWithRetry(() => import('./components/Portal/ReportsPage'), 'ReportsPage');

// Farmer File Upload — external submit page (the internal cross-farmer inbox now lives as the
// Communication page's "Uploaded Files" tab; /uploaded-files redirects there — see below).
const FarmerFileUploadPage = lazyWithRetry(() => import('./components/FarmerFiles/FarmerFileUploadPage'), 'FarmerFileUploadPage');

// Public pages (rendered outside the authenticated app shell)
const RegistrationPage = lazyWithRetry(() => import('./components/AgentPortal/RegistrationPage'), 'RegistrationPage');
const VerifyEmailPage = lazyWithRetry(() => import('./components/AgentPortal/VerifyEmailPage'), 'VerifyEmailPage');
const ForgotPasswordPage = lazyWithRetry(() => import('./components/Auth/ForgotPasswordPage'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyWithRetry(() => import('./components/Auth/ResetPasswordPage'), 'ResetPasswordPage');
const PublicSharePage = lazyWithRetry(() => import('./components/Share/PublicSharePage'), 'PublicSharePage');
const PublicShareLinkPage = lazyWithRetry(() => import('./components/Share/PublicShareLinkPageV2'), 'PublicShareLinkPage');
const PublicFertilizerProgramPage = lazyWithRetry(() => import('./components/Share/PublicFertilizerProgramPage'), 'PublicFertilizerProgramPage');

// Auth-aware redirect for the friendly /intake URL (eager — tiny, no chunk)
import { IntakeRedirect } from './components/Intake/IntakeRedirect';

// Services and hooks
import { menuIdToPath } from './utils/navigation';
import { RouteErrorBoundary } from './components/Layout/RouteErrorBoundary';

// ---------------------------------------------------------------------------
// Helper to wrap lazy-loaded components in Suspense
// ---------------------------------------------------------------------------

function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<RouteSuspenseFallback />}>{element}</Suspense>;
}

// ---------------------------------------------------------------------------
// Wrapper components for pages that need special props / hook access
// ---------------------------------------------------------------------------

function HybridNormsCalculationWrapper() {
  const navigate = useNavigate();
  return (
    <HybridNormsCalculationPage
      onNavigate={(menuId: string) => {
        const path = menuIdToPath(menuId);
        navigate(path);
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Full-width menu IDs - these pages render without the container max-width
// ---------------------------------------------------------------------------

const fullWidthMenuIds = new Set([
  // The analytics board is chart-heavy; full-width lets it reflow with the sidebar toggle instead of
  // being pinned at the 1920px Container cap on wide displays.
  'dashboard',
  'data-import',
  'user-management',
  'norms',
  'nutrient-norms',
  'hybrid-norms',
  'hybrid-norms-calculator',
  'hybrid-norms-view',
  'batches',
  'workspace',
  'farmers',
  'farms',
  'farm-plots',
  'entity-search',
  'agents',
  'labs',
  'crop-types',
  'crop-sub-types',
  'sample-types',
  'ratio-configurations',
  'ai-prompt',
  'message-templates',
  'dris-calculation-settings',
  'element-breakpoints',
  'product-factors',
  'recalculate-samples',
  'element-rankings',
  'compression-training',
  'legacy-calculator',
  'workflow-dashboard',
  'consolidated-invoicing',
  'sage-settings',
  'claude-settings',
  'role-management',
  'workflow-permissions',
  'menu-permissions',
  'access-grants',
  'report-audit',
  'notifications',
  'staging-review',
  'agent-portal',
  'agent-portal-workspace',
  // New External IA — Reports hosts the same full-width dashboard as agent-portal.
  'portal-reports',
  // SS External self-service — list + per-run workspace (a 100vh tabbed shell) run full-width.
  'portal-smart-sampling',
  'partner-portal',
  'partner-results',
  'partner-results-workspace',
  'partner-submit',
  'partner-entities',
  'partner-api-keys',
  'partner-workflow-new',
  'partner-workflows',
  'partner-products',
  'partner-team',
  'communication',
  'partner-communication',
  'smart-sampling',
  'data-extraction',
]);

function handle(menuId: string) {
  return { menuId, fullWidth: fullWidthMenuIds.has(menuId) };
}

// ---------------------------------------------------------------------------
// Router definition
// ---------------------------------------------------------------------------

// Sentry-instrumented createBrowserRouter (react-router v7 data router). Pairs with
// reactRouterV7BrowserTracingIntegration in services/sentry.ts to emit navigation/pageload
// transactions. When Sentry is disabled (no DSN) the wrapper is a transparent pass-through.
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
  // Public routes — rendered OUTSIDE the authenticated AppLayout shell so unauthenticated
  // visitors (self-registration, email verification) are not bounced to the login screen.
  {
    path: '/register',
    element: withSuspense(<RegistrationPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/verify-email',
    element: withSuspense(<VerifyEmailPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/forgot-password',
    element: withSuspense(<ForgotPasswordPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Landing page for the emailed reset link. The path AND the ?token= parameter are a contract
    // with UserManagementService.QueuePasswordResetEmailAsync — renaming either alone dead-ends
    // every link already in a user's inbox.
    path: '/reset-password',
    element: withSuspense(<ResetPasswordPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Friendly /intake URL. The form is now registration-gated (authed portal at /portal/intake):
    // authenticated visitors go straight there, everyone else to /register. Kept outside AppLayout
    // so a logged-out visitor isn't bounced to the login screen first.
    path: '/intake',
    element: <IntakeRedirect />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    // OTP-gated anonymous report share (T5). No auth; the page owns its loading/OTP/ready states.
    path: '/share/:token',
    element: withSuspense(<PublicSharePage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Public, anonymous ShareLink viewer (the marketing-grade concept viewer). No auth; the page owns
    // its landing/OTP/ready states. This is the sole public share viewer.
    path: '/s/:token',
    element: withSuspense(<PublicShareLinkPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // P5a — public, anonymous farmer view of an approved fertilizer program. No auth; the page owns its
    // landing/OTP/ready states. Sibling of /s/:token, rendered OUTSIDE the authenticated app shell.
    path: '/fp/:token',
    element: withSuspense(<PublicFertilizerProgramPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Default redirect
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // Dashboard — role-adaptive analytics board (entry point after login)
      {
        path: 'dashboard',
        element: withSuspense(<AnalyticsDashboard />),
        handle: handle('dashboard'),
      },

      // Agent Portal (external users) — index-free dashboard + per-batch workspace.
      // These render inside the auth shell; the menu is gated to the External role and the
      // backend doubly enforces access (grants + approval), so route exposure is harmless.
      {
        // Under the new External IA (billingEnabled) /portal is the landing → Home (plan P12/P15);
        // dark, it stays the reports dashboard (which itself bounces free-tier accounts to Home).
        path: 'portal',
        element: <BillingIaRedirect to="/portal/home">{withSuspense(<AgentPortalDashboard />)}</BillingIaRedirect>,
        handle: handle('agent-portal'),
      },
      // ---- New External IA (plan §4 / P12): Samples · Reports · Farms · Profile (+ Welcome, Buy).
      // Samples/Reports are the Phase 4b pages under the IA and today's pages while dark; Farms hosts
      // today's page until Phase 5; Profile/Buy are Phase 5/6a.
      {
        path: 'portal/samples',
        element: <BillingIaSwitch ia={withSuspense(<SamplesPage />)} dark={withSuspense(<MySubmissionsPage />)} />,
        handle: handle('portal-samples'),
      },
      {
        path: 'portal/reports',
        element: <BillingIaSwitch ia={withSuspense(<ReportsPage />)} dark={withSuspense(<AgentPortalDashboard />)} />,
        handle: handle('portal-reports'),
      },
      {
        // Phase 5: X6 Farms under the IA; the legacy My Farmers page while dark.
        path: 'portal/farms',
        element: <BillingIaSwitch ia={withSuspense(<FarmsPage />)} dark={withSuspense(<MyFarmersPage />)} />,
        handle: handle('portal-farms'),
      },
      {
        // Phase 5: X7/X8 Details · Billing · Security (?tab=).
        path: 'portal/profile',
        element: withSuspense(<PortalProfilePage />),
        handle: handle('portal-profile'),
      },
      // SS External self-service (Phase 4a): list + landing, new run, per-run workspace. Each route is
      // dark behind `smartSamplingExternalEnabled` (SmartSamplingExternalRoute → bounce to Home). The
      // `/new` static segment outranks `/:id`, but it is listed first for clarity.
      {
        path: 'portal/smart-sampling',
        element: (
          <SmartSamplingExternalRoute>{withSuspense(<ExternalSmartSamplingListPage />)}</SmartSamplingExternalRoute>
        ),
        handle: handle('portal-smart-sampling'),
      },
      {
        path: 'portal/smart-sampling/new',
        element: (
          <SmartSamplingExternalRoute>{withSuspense(<ExternalNewRunPage />)}</SmartSamplingExternalRoute>
        ),
        handle: handle('portal-smart-sampling'),
      },
      {
        path: 'portal/smart-sampling/:id',
        element: (
          <SmartSamplingExternalRoute>{withSuspense(<ExternalSmartSamplingWorkspace />)}</SmartSamplingExternalRoute>
        ),
        handle: handle('portal-smart-sampling'),
      },
      {
        // Phase 6a: X9/A6–A8 checkout under the IA. Dark (or a non-IA persona) gets an honest
        // "not available" — never a placeholder that promises a feature (plan P15).
        path: 'portal/buy',
        element: (
          <BillingIaSwitch
            ia={withSuspense(<BuyAnalysesPage />)}
            dark={<PortalPlaceholderPage title="Buy analyses" blurb="Buying analyses is not available at the moment." comingSoon={false} />}
          />
        ),
        handle: handle('portal-buy'),
      },
      {
        // Phase 6a: the gateway's return URL (`Billing:ReturnUrl`) — A9 Done / failed / cancelled.
        path: 'portal/buy/return',
        element: (
          <BillingIaSwitch
            ia={withSuspense(<BuyReturnPage />)}
            dark={<PortalPlaceholderPage title="Buy analyses" blurb="Buying analyses is not available at the moment." comingSoon={false} />}
          />
        ),
        handle: handle('portal-buy-return'),
      },
      {
        // A1 first-login welcome — the force-gate destination when billing is enabled (plan P16).
        path: 'portal/welcome',
        element: withSuspense(<WelcomePage />),
        handle: handle('portal-welcome'),
      },
      // Free-tier "Customer Home" — the guided landing for External accounts. A fresh zero-grant
      // External user landing on /portal is redirected here (see AgentPortalDashboard).
      {
        path: 'portal/home',
        element: withSuspense(<CustomerHome />),
        handle: handle('customer-home'),
      },
      // Free-tier Sample Intake (registration-gated): submit + My Submissions. These are the
      // baseline External surface; the batches/reports portal above appears only once the account
      // is granted data access.
      {
        path: 'portal/intake',
        element: withSuspense(<PortalSampleIntakePage />),
        handle: handle('intake-submit'),
      },
      {
        // Legacy nested free-text intake — kept for rollback until the redesign is live-validated.
        path: 'portal/intake-legacy',
        element: withSuspense(<PortalIntakePage />),
        handle: handle('intake-submit'),
      },
      {
        // Legacy path — redirects to /portal/samples under the new IA, renders as-is while dark.
        path: 'portal/submissions',
        element: <BillingIaRedirect to="/portal/samples">{withSuspense(<MySubmissionsPage />)}</BillingIaRedirect>,
        handle: handle('my-submissions'),
      },
      // Legacy Submit-hub route — the 3-pathway chooser now lives inline on the Customer Home landing
      // (<SubmitPathways/> in CustomerHome). Kept as a redirect so deep links and internal "submit
      // another"/onboarding "send samples" links still resolve.
      {
        path: 'portal/submit',
        element: <Navigate to="/portal/home" replace />,
      },
      // Pathway ② — "Analyse existing lab results": upload a report + resolved context, mint an intake
      // entry. Reached from the hub; not a sidebar item, but route-reachable via the free-tier floor.
      {
        path: 'portal/analyse',
        element: withSuspense(<AnalyseExistingResultsPage />),
        handle: handle('analyse-results'),
      },
      // Free-tier Farmer File Upload — submit files with free-text farmer names (deferred linkage).
      {
        path: 'portal/files',
        element: withSuspense(<FarmerFileUploadPage />),
        handle: handle('file-upload'),
      },
      // Free-tier "My Farmers" — owned Farmer → Farm → Field CRUD via /api/portal/profile.
      {
        // Legacy path — redirects to /portal/farms under the new IA, renders as-is while dark.
        path: 'portal/farmers',
        element: <BillingIaRedirect to="/portal/farms">{withSuspense(<MyFarmersPage />)}</BillingIaRedirect>,
        handle: handle('my-farmers'),
      },
      // Post-OTP onboarding — skippable first-run profile setup. Not a sidebar item; reached via the
      // CustomerHome "finish your profile" nudge (route reachable through the External free-tier floor).
      {
        path: 'portal/onboarding',
        element: withSuspense(<OnboardingPage />),
        handle: handle('onboarding'),
      },
      {
        path: 'portal/batches/:batchId',
        element: withSuspense(<AgentPortalWorkspace />),
        handle: handle('agent-portal-workspace'),
      },

      // Partner Portal (partner-lab users) — dashboard, submit, my entities, results, API keys.
      // Gated to the Partner role; the /api/partner/v1 backend independently enforces scope.
      {
        path: 'partner',
        element: withSuspense(<PartnerPortalDashboard />),
        handle: handle('partner-portal'),
      },
      {
        path: 'partner/submit',
        element: withSuspense(<PartnerSubmitPage />),
        handle: handle('partner-submit'),
      },
      {
        path: 'partner/entities',
        element: withSuspense(<PartnerEntitiesPage />),
        handle: handle('partner-entities'),
      },
      {
        path: 'partner/results',
        element: withSuspense(<PartnerResultsList />),
        handle: handle('partner-results'),
      },
      {
        path: 'partner/results/:batchId',
        element: withSuspense(<PartnerResultsWorkspace />),
        handle: handle('partner-results-workspace'),
      },
      {
        path: 'partner/api-keys',
        element: withSuspense(<PartnerApiKeysPage />),
        handle: handle('partner-api-keys'),
      },
      {
        path: 'partner/workflow/new',
        element: withSuspense(<PartnerWorkflowCreatePage />),
        handle: handle('partner-workflow-new'),
      },
      {
        path: 'partner/workflows',
        element: withSuspense(<PartnerWorkflowsPage />),
        handle: handle('partner-workflows'),
      },
      {
        // Interactive partner staging review — composed from the prop-driven admin steps via
        // partner-scoped hooks. Full-width; gated to Partner via the partner-workflow-new menu.
        path: 'partner/staging/review/:sessionId',
        element: withSuspense(<PartnerStagingReviewPage />),
        handle: { fullWidth: true },
      },
      {
        // Per-partner products self-service. Gated to the PartnerProductManager add-on role via
        // canAccessMenu('partner-products'); AppLayout renders <AccessDenied/> for a plain Partner.
        path: 'partner/products',
        element: withSuspense(<PartnerProductsPage />),
        handle: handle('partner-products'),
      },
      {
        // Partner team management. Gated to the PartnerAdmin add-on role via canAccessMenu('partner-team');
        // AppLayout renders <AccessDenied/> for a plain Partner.
        path: 'partner/team',
        element: withSuspense(<PartnerTeamPage />),
        handle: handle('partner-team'),
      },
      {
        // Consolidated partner Communication page (bookings + messages across the partner's links).
        path: 'partner/communication',
        element: withSuspense(<CommunicationPage portal="partner" />),
        handle: handle('partner-communication'),
      },

      // Batches
      {
        path: 'batches',
        element: withSuspense(<BatchesPage />),
        handle: handle('batches'),
      },
      {
        path: 'batches/:batchId',
        element: withSuspense(<WorkspacePage />),
        handle: { fullWidth: true },
      },
      {
        path: 'workspace/:batchId',
        element: withSuspense(<WorkspacePage />),
        handle: { fullWidth: true },
      },

      // Smart Sampling (boundary-driven sampling runs)
      {
        path: 'smart-sampling',
        element: withSuspense(<SmartSamplingListPage />),
        handle: handle('smart-sampling'),
      },
      {
        path: 'smart-sampling/:runId',
        element: withSuspense(<SmartSamplingWorkspace />),
        handle: { fullWidth: true },
      },

      // Fertilizer hub — tabbed surfaces under ONE `fertilizer-programs` capability. Programs is the
      // primary surface and owns the bare `/fertilizer-programs` default; the other tabs are nested,
      // deep-linkable URLs. Every route shares handle('fertilizer-programs') so the route guard matches
      // the single backend policy that guards all these endpoints.
      {
        // Programs list — the hub's 1st tab and the "Fertilizer" menu's landing page.
        path: 'fertilizer-programs',
        element: withSuspense(<FertilizerProgramsPage />),
        handle: handle('fertilizer-programs'),
      },
      {
        // P2-T3b: the editable crop nutrient-removal reference table is the hub's 2nd tab.
        path: 'fertilizer-programs/removal-bands',
        element: withSuspense(<FertilizerProgramsPage />),
        handle: handle('fertilizer-programs'),
      },
      {
        // Phase 1a: read-only product master is the hub's 3rd tab.
        path: 'fertilizer-programs/product-master',
        element: withSuspense(<FertilizerProgramsPage />),
        handle: handle('fertilizer-programs'),
      },
      {
        // Phase 1b: held-row adjudication queue is the hub's 4th tab.
        path: 'fertilizer-programs/adjudication',
        element: withSuspense(<FertilizerProgramsPage />),
        handle: handle('fertilizer-programs'),
      },
      {
        // Legacy alias: the pre-reorder Programs URL. Kept so old builder-back links (and any
        // bookmarks) still resolve — the hub falls it through to the Programs tab (bare default).
        path: 'fertilizer-programs/programs-list',
        element: withSuspense(<FertilizerProgramsPage />),
        handle: handle('fertilizer-programs'),
      },
      {
        // P2-T2: the Builder is a STANDALONE full-width route (the two-column layout needs the width).
        // Explicit `fullWidth: true` in the handle widens ONLY the Builder — `fertilizer-programs` is
        // deliberately NOT added to `fullWidthMenuIds` (that would widen the products/adjud/list tabs).
        path: 'fertilizer-programs/builder',
        element: withSuspense(<FertilizerProgramBuilderPage />),
        handle: { menuId: 'fertilizer-programs', fullWidth: true },
      },
      {
        path: 'fertilizer-programs/builder/:programId',
        element: withSuspense(<FertilizerProgramBuilderPage />),
        handle: { menuId: 'fertilizer-programs', fullWidth: true },
      },

      // Consolidated internal Communication page (cross-batch bookings + contact messages)
      {
        path: 'communication',
        element: withSuspense(<CommunicationPage portal="internal" />),
        handle: handle('communication'),
      },
      // Persistent sample-intake review surface (opened from Communication → Intake).
      {
        path: 'intake/review/:entryId',
        element: withSuspense(<IntakeReviewPage />),
        handle: handle('communication'),
      },
      // The internal cross-farmer "Uploaded Files" inbox is now the Communication page's "Uploaded
      // Files" tab. Kept as a redirect so existing bookmarks/links to /uploaded-files still resolve.
      {
        path: 'uploaded-files',
        element: <Navigate to="/communication?tab=files" replace />,
      },

      // Workflow dashboard (eager - primary navigation hub)
      {
        path: 'workflow',
        element: <WorkflowDashboard />,
        handle: handle('workflow-dashboard'),
      },
      {
        path: 'workflow/new',
        element: withSuspense(<WorkflowDetailPage />),
        handle: handle('workflow-dashboard'),
      },
      {
        path: 'workflow/:workflowId',
        element: withSuspense(<WorkflowDetailPage />),
        handle: handle('workflow-dashboard'),
      },

      // Consolidated Invoicing (Finance)
      {
        path: 'finance/consolidated',
        element: withSuspense(<ConsolidatedDashboard />),
        handle: handle('consolidated-invoicing'),
      },
      {
        path: 'finance/consolidated/:entityType/:entityId',
        element: withSuspense(<ConsolidatedClientDetailPage />),
        handle: handle('consolidated-invoicing'),
      },
      {
        path: 'finance/consolidated/:entityType/:entityId/period/:periodId',
        element: withSuspense(<ConsolidatedPeriodDetailPage />),
        handle: handle('consolidated-invoicing'),
      },
      {
        path: 'finance/consolidated/period/:periodId',
        element: withSuspense(<PeriodRedirect />),
        handle: handle('consolidated-invoicing'),
      },

      // Notifications
      {
        path: 'notifications',
        element: withSuspense(<NotificationsPage />),
        handle: handle('notifications'),
      },

      // Data Import
      {
        path: 'import',
        element: withSuspense(<TabbedImportPage />),
        handle: handle('data-import'),
      },

      // Staging Review
      {
        path: 'staging/review/:sessionId',
        element: withSuspense(<StagingReviewPage />),
        handle: { fullWidth: true },
      },

      // Legacy Calculator
      {
        path: 'legacy-calculator',
        element: withSuspense(<LegacyCalculatorPage />),
        handle: handle('legacy-calculator'),
      },

      // Hybrid Norms
      {
        path: 'hybrid-norms-view',
        element: withSuspense(<HybridNormsViewPage />),
        handle: handle('hybrid-norms-view'),
      },
      {
        path: 'hybrid-norms',
        element: withSuspense(<HybridNormsCalculationWrapper />),
        handle: handle('hybrid-norms-calculator'),
      },

      // Nutrient Norms
      {
        path: 'norms',
        element: withSuspense(<NormsPage />),
        handle: handle('nutrient-norms'),
      },

      // Farm Workspace v2 hub (docs/farm-workspace/redesign-plan.md §3.2) — one workspace per farmer,
      // scoped per farm (+ ?plot= sub-filter), with a `:tab` segment. FLAT entries (the fertilizer-programs
      // idiom): the route guard and sidebar selection read the LEAF match, so EVERY leaf carries
      // handle('entity-search') — the menu id is kept so existing role permissions carry over unchanged.
      // Path building/parsing lives in components/FarmWorkspace/workspaceRoutes.ts only.
      {
        path: 'farm-workspace',
        element: withSuspense(<FarmWorkspacePage />),
        handle: handle('entity-search'),
      },
      {
        path: 'farm-workspace/farmers/:farmerId',
        element: withSuspense(<FarmWorkspacePage />),
        handle: handle('entity-search'),
      },
      {
        path: 'farm-workspace/farmers/:farmerId/:tab',
        element: withSuspense(<FarmWorkspacePage />),
        handle: handle('entity-search'),
      },
      {
        path: 'farm-workspace/farmers/:farmerId/farms/:farmId',
        element: withSuspense(<FarmWorkspacePage />),
        handle: handle('entity-search'),
      },
      {
        path: 'farm-workspace/farmers/:farmerId/farms/:farmId/:tab',
        element: withSuspense(<FarmWorkspacePage />),
        handle: handle('entity-search'),
      },
      // The search-first v1 hub (#384/#391) and the older /agricultural-entities are superseded; every
      // legacy URL (incl. every `entity-search/*` sub-path) redirects for the bookmark period. The
      // table lives in workspaceRoutes.ts (LEGACY_HUB_PATHS) so the redirect test pins the same entries.
      // handle('entity-search') is kept so the guard evaluates the same capability before redirecting.
      ...LEGACY_HUB_PATHS.map((path) => ({
        path,
        element: <Navigate to={FARM_WORKSPACE_BASE} replace />,
        handle: handle('entity-search'),
      })),

      // Settings pages
      {
        path: 'settings/ai-prompt',
        element: withSuspense(<SettingsPage />),
        handle: handle('ai-prompt'),
      },
      {
        path: 'settings/templates',
        element: withSuspense(<MessageTemplatesPage />),
        handle: handle('message-templates'),
      },
      {
        path: 'settings/compression-training',
        element: withSuspense(<CompressionTrainingPage />),
        handle: handle('compression-training'),
      },
      {
        path: 'settings/dris-calculation',
        element: withSuspense(<DrisCalculationSettingsPage />),
        handle: handle('dris-calculation-settings'),
      },
      {
        path: 'settings/element-breakpoints',
        element: withSuspense(<HybridElementBreakpointsTab />),
        handle: handle('element-breakpoints'),
      },
      {
        path: 'element-rankings',
        element: withSuspense(<ElementRankingsPage />),
        handle: handle('element-rankings'),
      },
      {
        path: 'settings/sage',
        element: withSuspense(<SageSettingsPage />),
        handle: handle('sage-settings'),
      },
      {
        path: 'settings/claude',
        element: withSuspense(<ClaudeSettingsPage />),
        handle: handle('claude-settings'),
      },
      {
        path: 'settings/calendar',
        element: withSuspense(<CalendarSettingsPage />),
        handle: handle('calendar-settings'),
      },
      {
        path: 'settings/product-factors',
        element: withSuspense(<HybridProductFactorsTab />),
        handle: handle('product-factors'),
      },
      {
        path: 'settings/recalculate-samples',
        element: withSuspense(<RecalculateSamplesPage />),
        handle: handle('recalculate-samples'),
      },
      {
        // SuperAdmin bulk data-extraction (flat CSV + AI bundle). Backend authz is SuperAdmin-only;
        // the menu entry hides it from everyone else.
        path: 'settings/data-extraction',
        element: withSuspense(<DataExtractionPage />),
        handle: handle('data-extraction'),
      },

      // User Management
      {
        path: 'user-management',
        element: withSuspense(<UserManagementPage />),
        handle: handle('user-management'),
      },

      // Administration
      {
        path: 'admin/roles',
        element: withSuspense(<RoleManagement />),
        handle: handle('role-management'),
      },
      {
        path: 'admin/workflow-permissions',
        element: withSuspense(<WorkflowPermissions />),
        handle: handle('workflow-permissions'),
      },
      {
        path: 'admin/menu-permissions',
        element: withSuspense(<MenuPermissions />),
        handle: handle('menu-permissions'),
      },
      {
        path: 'admin/access-grants',
        element: withSuspense(<AccessGrants />),
        handle: handle('access-grants'),
      },
      {
        path: 'admin/report-audit',
        element: withSuspense(<ReportAccessAudit />),
        handle: handle('report-audit'),
      },
      {
        // SuperAdmin duplicate-DETECTION report (Plan C, Layer 4). Backend authz is SuperAdmin-only;
        // the menu entry hides it otherwise and the page also fails closed on the client.
        path: 'admin/duplicate-scan',
        element: withSuspense(<DuplicateScanPage />),
        handle: handle('duplicate-scan'),
      },
      {
        // SuperAdmin billing catalog (D11, §4.3): edit product price/currency/active. Backend authz is
        // SuperAdmin-only AND the External boundary denies it; the menu hides it otherwise and the page
        // also fails closed on the client.
        path: 'admin/billing-products',
        element: withSuspense(<BillingProductsPage />),
        handle: handle('billing-products'),
      },

      // Profile
      {
        path: 'profile',
        element: withSuspense(<ProfilePage />),
        handle: handle('profile'),
      },

      // Entity list pages (no menu items, navigated to from code)
      {
        path: 'farmers',
        element: withSuspense(<FarmersList />),
        handle: handle('farmers'),
      },
      {
        path: 'farms',
        element: withSuspense(<FarmsList />),
        handle: handle('farms'),
      },
      {
        path: 'farm-plots',
        element: withSuspense(<FarmPlotsList />),
        handle: handle('farm-plots'),
      },

      // People & Organizations
      {
        path: 'agents',
        element: withSuspense(<AgentsList />),
        handle: handle('agents'),
      },
      {
        path: 'labs',
        element: withSuspense(<LabsList />),
        handle: handle('labs'),
      },

      // Science & Classification
      {
        path: 'crop-types',
        element: withSuspense(<CropTypesList />),
        handle: handle('crop-types'),
      },
      {
        path: 'crop-sub-types',
        element: withSuspense(<CropSubTypesList />),
        handle: handle('crop-sub-types'),
      },
      {
        path: 'sample-types',
        element: withSuspense(<SampleTypesList />),
        handle: handle('sample-types'),
      },
      {
        path: 'ratio-configurations',
        element: withSuspense(<RatioConfigurationsList />),
        handle: handle('ratio-configurations'),
      },

      // Backward-compatible redirects
      { path: 'lookup-tables', element: <Navigate to="/crop-types" replace /> },
      { path: 'settings/element-rankings', element: <Navigate to="/element-rankings" replace /> },

      // Catch-all redirect
      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
]);
